'use client';

import { useState } from 'react';
import {
    HandCoins,
    TrendingUp,
    Filter,
    Search,
    ChevronDown,
    Calendar,
    ArrowUpRight,
    User,
    FileText,
    Banknote,
    Plus,
    Eye,
    Edit,
    Trash,
    CheckCircle2,
    XCircle,
    X
} from 'lucide-react';
import Link from 'next/link';

export default function SetoranMasukPage() {
    const schoolName = "PAUD Al-Azhar 1";
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState<'all' | 'thisMonth'>('thisMonth');
    const [statusFilter, setStatusFilter] = useState<'all' | 'Draft' | 'Menunggu Verifikasi' | 'Terverifikasi' | 'Ditolak'>('all');
    const [categoryFilter, setCategoryFilter] = useState('all');

    // Helper functions
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount);
    };

    const parseAmount = (amountStr: string) => {
        return parseInt(amountStr.replace(/[^0-9]/g, ''), 10);
    };

    // Mock Data Setoran Masuk
    const [receipts, setReceipts] = useState([
        {
            id: 'STM-2024-001',
            date: '2024-02-09',
            source: 'Budi Santoso (Siswa)',
            description: 'SPP Februari 2024',
            category: 'SPP',
            amount: 'Rp 500.000',
            rekening: 'Bank BSI',
            status: 'Menunggu Verifikasi',
            paymentMethod: 'Transfer'
        },
        {
            id: 'STM-2024-002',
            date: '2024-02-08',
            source: 'Donatur (Hamba Allah)',
            description: 'Sumbangan Pembangunan Masjid',
            category: 'Donasi',
            amount: 'Rp 2.000.000',
            rekening: 'Bank Mandiri',
            status: 'Terverifikasi',
            paymentMethod: 'Transfer'
        },
        {
            id: 'STM-2024-003',
            date: '2024-02-08',
            source: 'Siti Aminah (Wali)',
            description: 'Uang Kegiatan Outbound',
            category: 'Kegiatan',
            amount: 'Rp 350.000',
            rekening: 'Kas Tunai',
            status: 'Draft',
            paymentMethod: 'Cash'
        },
        {
            id: 'STM-2024-004',
            date: '2024-02-05',
            source: 'Ahmad (Siswa)',
            description: 'SPP Januari 2024 (Tunggakan)',
            category: 'SPP',
            amount: 'Rp 500.000',
            rekening: 'Bank BSI',
            status: 'Ditolak',
            paymentMethod: 'Transfer'
        },
        {
            id: 'STM-2024-005',
            date: '2024-02-01',
            source: 'Koperasi Sekolah',
            description: 'Bagi Hasil Kantin',
            category: 'Lainnya',
            amount: 'Rp 1.200.000',
            rekening: 'Ka Tunai',
            status: 'Terverifikasi',
            paymentMethod: 'Cash'
        }
    ]);

    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
    const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [rejectReason, setRejectReason] = useState('');

    const filteredReceipts = receipts.filter(item => {
        const matchesSearch = item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.source.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesDate = dateFilter === 'all' || (dateFilter === 'thisMonth' && item.date.startsWith('2024-02'));
        const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
        const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;

        return matchesSearch && matchesDate && matchesStatus && matchesCategory;
    });

    const totalAmount = receipts
        .filter(r => r.status !== 'Ditolak') // Count all except rejected? Or just verified? Let's count potential money (Verified + Pending).
        .reduce((acc, curr) => acc + parseAmount(curr.amount), 0);

    const handleDeleteClick = (id: string) => {
        setSelectedId(id);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        if (selectedId) {
            setReceipts(prev => prev.filter(item => item.id !== selectedId));
            setIsDeleteModalOpen(false);
            setSelectedId(null);
            console.log(`Item with ID ${selectedId} deleted.`);
        }
    };

    const handleEditClick = (id: string) => { console.log(`Edit clicked for ${id}`); };

    const handleVerifyClick = (id: string) => {
        setSelectedId(id);
        setIsVerifyModalOpen(true);
    };

    const confirmVerify = () => {
        if (selectedId) {
            setReceipts(prev => prev.map(item =>
                item.id === selectedId ? { ...item, status: 'Terverifikasi' } : item
            ));
            setIsVerifyModalOpen(false);
            setSelectedId(null);
            console.log(`Item with ID ${selectedId} verified.`);
        }
    };

    const handleRejectClick = (id: string) => {
        setSelectedId(id);
        setRejectReason('');
        setIsRejectModalOpen(true);
    };

    const confirmReject = () => {
        if (selectedId && rejectReason.trim()) {
            setReceipts(prev => prev.map(item =>
                item.id === selectedId ? { ...item, status: 'Ditolak' } : item
            ));
            setIsRejectModalOpen(false);
            setSelectedId(null);
            console.log(`Item with ID ${selectedId} rejected. Reason: ${rejectReason}`);
        }
    };

    const handleDetailClick = (id: string) => {
        setSelectedId(id);
        setIsDetailModalOpen(true);
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'Terverifikasi': return 'bg-success-soft text-success border-success/10';
            case 'Menunggu Verifikasi': return 'bg-warning-soft text-warning border-warning/10';
            case 'Draft': return 'bg-muted/10 text-muted border-muted/20';
            case 'Ditolak': return 'bg-error-soft text-error border-error/10';
            default: return 'bg-secondary-soft text-secondary';
        }
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700 relative">
            {/* Header Area */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-success-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="relative z-10 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                                <Link href="/sekolah" className="hover:text-primary transition-all">Keuangan</Link>
                                <span className="text-muted/50">•</span>
                                <span className="text-foreground">Setoran Masuk</span>
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 rounded-2xl bg-success-soft/50 border border-success/10 flex items-center justify-center text-success shadow-inner">
                                    <HandCoins className="w-8 h-8" />
                                </div>
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight whitespace-nowrap">Setoran Masuk</h1>
                                    <p className="text-secondary font-medium flex items-center gap-2">
                                        <TrendingUp className="w-3.5 h-3.5 text-success" /> Penerimaan Dana Operasional Sekolah • {schoolName}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <Link href="/sekolah/setoran-masuk/tambah">
                            <button className="flex items-center gap-3 px-6 py-3.5 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all">
                                <Plus className="w-4 h-4" /> Input Setoran Masuk
                            </button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="p-6 rounded-3xl bg-background border border-border-main shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Setoran (Est)</span>
                        <div className="p-2 bg-primary-soft/30 rounded-lg text-primary"><TrendingUp className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black text-foreground">{formatCurrency(totalAmount)}</p>
                </div>
                <div className="p-6 rounded-3xl bg-background border border-border-main shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Jumlah Transaksi</span>
                        <div className="p-2 bg-secondary-soft/30 rounded-lg text-secondary"><FileText className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black text-foreground">{receipts.length} <span className="text-sm font-medium text-muted">Trx</span></p>
                </div>
                <div className="p-6 rounded-3xl bg-background border border-border-main shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Menunggu Verifikasi</span>
                        <div className="p-2 bg-warning-soft/30 rounded-lg text-warning"><User className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black text-foreground">{receipts.filter(r => r.status === 'Menunggu Verifikasi').length} <span className="text-sm font-medium text-muted">Perlu Dicek</span></p>
                </div>
                <div className="p-6 rounded-3xl bg-background border border-border-main shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Terverifikasi</span>
                        <div className="p-2 bg-success-soft/30 rounded-lg text-success"><CheckCircle2 className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black text-foreground">{receipts.filter(r => r.status === 'Terverifikasi').length} <span className="text-sm font-medium text-muted">Valid</span></p>
                </div>
            </div>

            {/* Filter & Search */}
            <div className="bg-surface border border-border-main p-4 rounded-3xl shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="relative flex-1 group w-full md:max-w-xs">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Cari ID / Siswa / Penyetor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-background/50 border border-border-main rounded-2xl py-3.5 pl-12 pr-6 text-xs font-semibold focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all outline-none"
                        />
                    </div>

                    {/* Filters Group */}
                    <div className="flex flex-wrap gap-3 flex-1">
                        {/* Status Filter */}
                        <div className="relative group min-w-[140px]">
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value as any)}
                                className="w-full pl-4 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/30 appearance-none cursor-pointer hover:border-primary/30 transition-all text-muted"
                            >
                                <option value="all">Semua Status</option>
                                <option value="Draft">Draft</option>
                                <option value="Menunggu Verifikasi">Menunggu Verifikasi</option>
                                <option value="Terverifikasi">Terverifikasi</option>
                                <option value="Ditolak">Ditolak</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
                        </div>

                        {/* Jenis Setoran Filter */}
                        <div className="relative group min-w-[150px]">
                            <select
                                value={categoryFilter}
                                onChange={(e) => setCategoryFilter(e.target.value)}
                                className="w-full pl-4 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/30 appearance-none cursor-pointer hover:border-primary/30 transition-all text-muted"
                            >
                                <option value="all">Semua Jenis</option>
                                <option value="SPP">SPP</option>
                                <option value="Kegiatan">Kegiatan</option>
                                <option value="Donasi">Donasi</option>
                                <option value="Lainnya">Lainnya</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
                        </div>

                        {/* Date Range Picker Placeholder */}
                        <button
                            onClick={() => setDateFilter(prev => prev === 'all' ? 'thisMonth' : 'all')}
                            className={`flex items-center gap-3 px-5 py-3.5 border rounded-2xl text-[10px] font-bold uppercase tracking-widest transition-all ${dateFilter === 'thisMonth'
                                ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20'
                                : 'bg-background border-border-main text-muted hover:border-primary/30'
                                }`}
                        >
                            <Calendar className="w-4 h-4" />
                            {dateFilter === 'thisMonth' ? 'Bulan Ini' : 'Pilih Periode'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Transactions Table */}
            <div className="bg-surface border border-border-main rounded-3xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[180px]">ID & Tanggal</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Sumber & Keterangan</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px]">Rekening</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px] text-right">Nominal</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px] text-center">Status</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px] text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main/50">
                            {filteredReceipts.length > 0 ? filteredReceipts.map((item, idx) => (
                                <tr key={idx} className="hover:bg-primary-soft/5 transition-colors group">
                                    <td className="px-6 py-4">
                                        <div>
                                            <p className="text-xs font-black text-primary group-hover:underline">{item.id}</p>
                                            <div className="flex items-center gap-1.5 mt-1 text-muted">
                                                <Calendar className="w-3 h-3" />
                                                <span className="text-[10px] font-bold uppercase">{item.date}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 space-y-1">
                                        <p className="text-xs font-bold text-foreground">{item.source}</p>
                                        <p className="text-[11px] text-muted">{item.description}</p>
                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-background border border-border-main text-[9px] font-bold text-secondary uppercase tracking-wider">
                                            {item.category}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-xs font-bold text-foreground">{item.rekening}</span>
                                            <span className="text-[10px] text-muted uppercase">{item.paymentMethod}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <p className="text-sm font-black text-success tabular-nums">{item.amount}</p>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${getStatusBadge(item.status)}`}>
                                            {item.status}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex items-center justify-center gap-2">
                                            {item.status === 'Draft' && (
                                                <>
                                                    <button onClick={() => handleEditClick(item.id)} className="p-2 rounded-xl text-muted hover:text-warning hover:bg-warning-soft/30 transition-all" title="Edit Data"><Edit className="w-4 h-4" /></button>
                                                    <button onClick={() => handleDeleteClick(item.id)} className="p-2 rounded-xl text-muted hover:text-error hover:bg-error-soft/30 transition-all" title="Hapus Data"><Trash className="w-4 h-4" /></button>
                                                </>
                                            )}
                                            {item.status === 'Menunggu Verifikasi' && (
                                                <>
                                                    <button onClick={() => handleVerifyClick(item.id)} className="p-2 rounded-xl text-success hover:bg-success-soft/30 transition-all" title="Verifikasi"><CheckCircle2 className="w-4 h-4" /></button>
                                                    <button onClick={() => handleRejectClick(item.id)} className="p-2 rounded-xl text-error hover:bg-error-soft/30 transition-all" title="Tolak"><XCircle className="w-4 h-4" /></button>
                                                    <button onClick={() => handleDetailClick(item.id)} className="p-2 rounded-xl text-muted hover:text-primary hover:bg-primary-soft/30 transition-all" title="Lihat Detail"><Eye className="w-4 h-4" /></button>
                                                </>
                                            )}
                                            {(item.status === 'Terverifikasi' || item.status === 'Ditolak') && (
                                                <button onClick={() => handleDetailClick(item.id)} className="px-3 py-1.5 rounded-xl bg-background border border-border-main text-[10px] font-bold text-muted hover:text-primary hover:border-primary/50 transition-all uppercase tracking-wider">
                                                    Detail
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={6} className="px-6 py-10 text-center text-muted font-medium">
                                        Tidak ada data yang ditemukan
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface p-8 rounded-3xl shadow-2xl max-w-sm w-full space-y-6 animate-in zoom-in-95 duration-200">
                        <div className="text-center space-y-2">
                            <div className="w-12 h-12 bg-error-soft text-error rounded-full flex items-center justify-center mx-auto mb-4">
                                <Trash className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg font-bold text-foreground">Hapus Data?</h3>
                            <p className="text-sm text-muted">Apakah Anda yakin ingin menghapus data setoran ini? Tindakan ini tidak dapat dibatalkan.</p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="flex-1 px-4 py-3 bg-background border border-border-main rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-secondary-soft/50 transition-all"
                            >
                                Batal
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="flex-1 px-4 py-3 bg-error text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:shadow-lg hover:shadow-error/20 transition-all"
                            >
                                Hapus
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Verifikasi Modal */}
            {isVerifyModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface p-8 rounded-3xl shadow-2xl max-w-sm w-full space-y-6 animate-in zoom-in-95 duration-200">
                        <div className="text-center space-y-2">
                            <div className="w-12 h-12 bg-success-soft text-success rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg font-bold text-foreground">Verifikasi Data?</h3>
                            <p className="text-sm text-muted">Apakah Anda yakin ingin memverifikasi setoran ini? Status akan berubah menjadi <b>Terverifikasi</b>.</p>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setIsVerifyModalOpen(false)}
                                className="flex-1 px-4 py-3 bg-background border border-border-main rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-secondary-soft/50 transition-all"
                            >
                                Batal
                            </button>
                            <button
                                onClick={confirmVerify}
                                className="flex-1 px-4 py-3 bg-success text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:shadow-lg hover:shadow-success/20 transition-all"
                            >
                                Verifikasi
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Tolak Modal */}
            {isRejectModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface p-8 rounded-3xl shadow-2xl max-w-sm w-full space-y-6 animate-in zoom-in-95 duration-200">
                        <div className="text-center space-y-2">
                            <div className="w-12 h-12 bg-error-soft text-error rounded-full flex items-center justify-center mx-auto mb-4">
                                <XCircle className="w-6 h-6" />
                            </div>
                            <h3 className="text-lg font-bold text-foreground">Tolak Setoran?</h3>
                            <p className="text-sm text-muted">Mohon berikan alasan penolakan untuk data setoran ini.</p>
                        </div>

                        <textarea
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                            placeholder="Contoh: Bukti transfer buram, nominal tidak sesuai, dll."
                            className="w-full h-24 p-4 bg-background border border-border-main rounded-2xl text-sm focus:ring-2 focus:ring-error/20 focus:border-error transition-all outline-none resize-none"
                        />

                        <div className="flex gap-3">
                            <button
                                onClick={() => setIsRejectModalOpen(false)}
                                className="flex-1 px-4 py-3 bg-background border border-border-main rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-secondary-soft/50 transition-all"
                            >
                                Batal
                            </button>
                            <button
                                onClick={confirmReject}
                                disabled={!rejectReason.trim()}
                                className="flex-1 px-4 py-3 bg-error text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:shadow-lg hover:shadow-error/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Tolak Data
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Detail Modal */}
            {isDetailModalOpen && selectedId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface p-8 rounded-3xl shadow-2xl max-w-2xl w-full space-y-8 animate-in zoom-in-95 duration-200 relative">
                        <button
                            onClick={() => setIsDetailModalOpen(false)}
                            className="absolute top-6 right-6 p-2 rounded-full hover:bg-secondary-soft/50 text-muted hover:text-foreground transition-all"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-4 border-b border-border-main pb-6">
                            <div className="w-14 h-14 bg-primary-soft/30 rounded-2xl flex items-center justify-center text-primary">
                                <FileText className="w-7 h-7" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-foreground">Detail Setoran</h3>
                                <p className="text-sm text-muted font-medium">#{selectedId}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Tanggal Setoran</label>
                                <p className="text-sm font-bold text-foreground">{receipts.find(r => r.id === selectedId)?.date}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Kategori</label>
                                <div className="flex">
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-background border border-border-main text-[10px] font-bold text-secondary uppercase tracking-wider">
                                        {receipts.find(r => r.id === selectedId)?.category}
                                    </span>
                                </div>
                            </div>
                            <div className="space-y-1 md:col-span-2">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Diterima Dari</label>
                                <p className="text-sm font-bold text-foreground">{receipts.find(r => r.id === selectedId)?.source}</p>
                            </div>
                            <div className="space-y-1 md:col-span-2">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Keterangan</label>
                                <p className="text-sm font-medium text-foreground bg-background/50 p-4 rounded-xl border border-border-main">
                                    {receipts.find(r => r.id === selectedId)?.description}
                                </p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Metode Pembayaran</label>
                                <p className="text-sm font-bold text-foreground">{receipts.find(r => r.id === selectedId)?.paymentMethod}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Rekening Tujuan</label>
                                <p className="text-sm font-bold text-foreground">{receipts.find(r => r.id === selectedId)?.rekening}</p>
                            </div>

                            <div className="space-y-1 md:col-span-2 pt-4 border-t border-border-main flex items-center justify-between">
                                <div>
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Status</label>
                                    <div className="mt-1">
                                        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wide border ${getStatusBadge(receipts.find(r => r.id === selectedId)?.status || '')}`}>
                                            {receipts.find(r => r.id === selectedId)?.status}
                                        </span>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Nominal</label>
                                    <p className="text-2xl font-black text-success tabular-nums">{receipts.find(r => r.id === selectedId)?.amount}</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={() => setIsDetailModalOpen(false)}
                                className="w-full px-4 py-3 bg-secondary-soft/20 text-secondary hover:bg-secondary-soft/40 rounded-xl text-xs font-bold uppercase tracking-widest transition-all"
                            >
                                Tutup Detail
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
